import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions
} from "@tanstack/react-query";
import { ZoomAuthLoginResponse } from "../../containers/Home/GoogleAndZoom/types";
import $api from "../api/$api";
import {
  TDataToken,
  TGetZoomEventQueries,
  TGetZoomEventQueriesSelfHosted,
  TGetZoomMeetingsQueries,
  TGetZoomUserQueries,
  TMeetingResponse,
  TSkipZoomToggleInterviewSetupTip,
  TToggleAutoZoomPayload,
  ZoomMeeting
} from "./types";
const linxURl = process.env.NEXT_PUBLIC_LINX_URL;

// zoom auth
const zoomAuth = async (
  userID: string,
  teamID: string
): Promise<ZoomAuthLoginResponse> => {
  const url = `${linxURl}/integrations/zoom/auth?userID=${userID}&teamID=${teamID}`;

  return new Promise((resolve, reject) => {
    const zoomWindow = window.open(url, "_blank", "width=500,height=800");

    if (!zoomWindow) {
      reject(new Error("Failed to open Zoom authentication window"));
      return;
    }

    const messageListener = (event: MessageEvent) => {
      if (event.data.type === "zoom-auth") {
        resolve({
          zoomUserUUID: event.data.zoomUserUUID,
          isPermissionAllowed: event.data.isPermissionAllowed,
          isLicensedUser:event.data.isLicensedUser,
          errorMessage: event.data.errorMessage,
          isSuccess: event.data.isSuccess,
          type: event.data.type
        });

        window.removeEventListener("message", messageListener);
      }
    };

    window.addEventListener("message", messageListener, false);
  });
};

export const useZoomLogin = (
  config?: UseMutationOptions<any, unknown, unknown>
) => {
  return useMutation({
    mutationFn: ({ userID, teamID }: { userID: string; teamID: string }) =>
      zoomAuth(userID, teamID),
    ...config
  });
};

// get zoom user
export const getZoomUser = (data: TDataToken, queries: TGetZoomUserQueries) => {
  return $api
    .get(`/integrations/zoom/user`, {
      // @ts-ignore
      token: data.accessToken,
      params: { ...queries }
    })
    .then((response) => {
      return response;
    });
};

export const useGetZoomUser = (
  data: TDataToken,
  queries: TGetZoomUserQueries,
  config?: {}
) => {
  return useQuery({
    queryKey: ["zoom-user", queries],
    queryFn: () => getZoomUser(data, queries),
    ...(config as UseQueryOptions<any>)
  });
};

// get events by zoom userid
export const getEventByZoomUserUUID = (
  data: TDataToken,
  queries: TGetZoomEventQueries
) => {
  return $api
    .get(`/integrations/zoom/meetings/shouldBeRecorded`, {
      // @ts-ignore
      token: data.accessToken,
      params: { ...queries }
    })
    .then((response) => {
      return response;
    });
};

export const useGetEventByZoomUserUUID = (
  data: TDataToken,
  queries: TGetZoomEventQueries,
  config?: {}
) => {
  return useQuery({
    queryKey: ["zoom-events", queries],
    queryFn: () => getEventByZoomUserUUID(data, queries),
    ...(config as UseQueryOptions<any>)
  });
};

// get events by zoom userid and meeting code
export const getEventByZoomUserUUIDAndMeetingCode = (
  data: TDataToken,
  queries: TGetZoomEventQueriesSelfHosted
) => {
  return $api
    .get(`/integrations/zoom/meetings/code`, {
      token: data.accessToken,
      params: { ...queries }
    })
    .then((response) => {
      return response;
    });
};

export const useGetEventByZoomUserUUIDAndMeetingCode = (
  data: TDataToken,
  queries: TGetZoomEventQueriesSelfHosted,
  config?: {}
) => {
  return useQuery({
    queryKey: ["zoom-events-selfhosted", queries],
    queryFn: () => getEventByZoomUserUUIDAndMeetingCode(data, queries),
    ...(config as UseQueryOptions<any>)
  });
};


export const postSkipZoomToggleInterviewSetupTip = async (
  data: TDataToken,
  payload: TSkipZoomToggleInterviewSetupTip
) => {
  return $api
    .patch("/user/update", {...payload}, {
      token: data.accessToken
    })
    .then((response) => {
      return response;
    });
};

export const usePostSkipZoomToggleInterviewSetupTip = (
  data: TDataToken,
  config?: UseMutationOptions<any, unknown, TSkipZoomToggleInterviewSetupTip>
) => {
  return useMutation({
    mutationFn: (payload: TSkipZoomToggleInterviewSetupTip) =>
      postSkipZoomToggleInterviewSetupTip(data, payload),
    ...config
  });
};

// toggle auto zoom meeting processing
export const toggleAutoZoomMeetingProcessing = async (
  data: TDataToken,
  payload: TToggleAutoZoomPayload
) => {
  const { queries, ...rest } = payload;

  const meetingInfo = (await $api
    .get(`/integrations/zoom/meetings/code`, {
      // @ts-ignore
      token: data.accessToken,
      params: { ...queries }
    })
    .then((response) => {
      return response;
    })) as any;

  const saveZoomMeetingRequest: ZoomMeeting = {
    zoomMeetingID: meetingInfo.id,
    appInterviewID: rest.appInterviewID,
    topic: meetingInfo.topic,
    hostUUID: meetingInfo.host_id,
    startTime:
      meetingInfo.start_time ??
      new Date(rest.googleEventStartTime).toISOString(),
    shouldBeRecorded: !rest.currentToggleState,
    appTeamID: rest.appTeamID
  };

  return $api
    .post("/integrations/zoom/meetings", saveZoomMeetingRequest, {
      // @ts-ignore
      token: data.accessToken
    })
    .then((response) => {
      return response;
    });
};

export const useToggleAutoZoomMeetingProcessing = (
  data: TDataToken,
  config?: UseMutationOptions<any, unknown, TToggleAutoZoomPayload>
) => {
  return useMutation({
    mutationFn: (payload: TToggleAutoZoomPayload) =>
      toggleAutoZoomMeetingProcessing(data, payload),
    ...config
  });
};

// get zoom meetings
export const getZoomMeetings = (
  data: TDataToken,
  queries: TGetZoomMeetingsQueries
): Promise<TMeetingResponse[]> => {
  return $api.get(`/integrations/zoom/meetings-recordings`, {
    // @ts-ignore
    token: data.accessToken,
    params: { ...queries }
  });
};

export const useGetZoomMeetings = (
  data: TDataToken,
  queries: TGetZoomMeetingsQueries,
  config?: UseQueryOptions<TMeetingResponse[]>
) => {
  return useQuery({
    queryKey: ["zoom-meetings", queries],
    queryFn: () => getZoomMeetings(data, queries),
    ...config
  });
};
